import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { Spinner } from 'src/components/ui/spinner';
import { T, Var, useGT } from 'gt-react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import useExtractedPurchaseOrder, {
  ExtractionResultDTO,
} from 'src/api/core/inbound-emails/useExtractedPurchaseOrder';
import { Grid } from 'src/components/common/grid';

interface ExtractedDataTabProps {
  inboundEmailId?: string;
  hasAttachments: boolean;
}

const ExtractedDataTab: React.FC<ExtractedDataTabProps> = ({
  inboundEmailId,
  hasAttachments,
}) => {
  const gt = useGT();
  const [selectedExtraction, setSelectedExtraction] = useState<number>(0);

  const {
    data: extractionData,
    isLoading: isLoadingExtraction,
    error: extractionError,
  } = useExtractedPurchaseOrder(inboundEmailId || '', !!inboundEmailId);

  console.log({ extractionData });

  if (!hasAttachments) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          <T context="empty state in extracted data tab when email has no attachments">
            No attachments found
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  if (!inboundEmailId) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          <T context="empty state in extracted data tab when no email is selected">
            No email selected
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  if (isLoadingExtraction) {
    return (
      <MDBox display="flex" justifyContent="center" p={4}>
        <Spinner />
      </MDBox>
    );
  }

  if (extractionError) {
    return (
      <MDBox
        p={3}
        border="1px solid"
        borderColor="error.main"
        borderRadius="borderRadius"
        color="error.main"
      >
        <MDTypography variant="body2">
          <T context="error message in extracted data tab when API call to load extraction results fails">
            Error loading extracted data: <Var>{extractionError.message}</Var>
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  if (!extractionData?.extractions?.length) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          <T context="empty state in extracted data tab when extraction returned no results">
            No extracted data found
          </T>
        </MDTypography>
      </MDBox>
    );
  }

  const renderExtractionDetails = (extraction: ExtractionResultDTO) => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox mb={2}>
              <MDTypography variant="subtitle2" mb={1}>
                <T context="section heading for AI extraction metadata like model and status">
                  Extraction Details
                </T>
              </MDTypography>
              <MDTypography variant="body2">
                <T context="label and value showing which AI model was used for data extraction">
                  Model: <Var>{extraction.modelUsed}</Var>
                </T>
              </MDTypography>
              <MDTypography variant="body2">
                <T context="label and value showing the current status of the data extraction">
                  Status: <Var>{extraction.status}</Var>
                </T>
              </MDTypography>
              <MDTypography variant="body2">
                <T context="label and value showing when the data extraction was created">
                  Created:{' '}
                  <Var>{new Date(extraction.createdAt).toLocaleString()}</Var>
                </T>
              </MDTypography>
            </MDBox>
          </Grid>

          {extraction.classification && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  <T context="section heading for document classification results from AI extraction">
                    Classification
                  </T>
                </MDTypography>
                <MDTypography variant="body2">
                  <T context="label and value showing the document classification result, e.g. invoice or receipt">
                    Result:{' '}
                    <Var>{extraction.classification.classificationResult}</Var>
                  </T>
                </MDTypography>
                <MDTypography variant="body2">
                  <T context="label and value showing the AI confidence score for the classification">
                    Confidence:{' '}
                    <Var>
                      {String(extraction.classification.confidenceScore)}
                    </Var>
                  </T>
                </MDTypography>
                <MDTypography variant="body2">
                  <T context="label and value showing which AI model was used for document classification">
                    Model: <Var>{extraction.classification.modelUsed}</Var>
                  </T>
                </MDTypography>
              </MDBox>
            </Grid>
          )}

          {extraction.errorDetails && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  <T context="section heading for error details when data extraction encountered an error">
                    Error Details
                  </T>
                </MDTypography>
                <pre
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {JSON.stringify(extraction.errorDetails, null, 2)}
                </pre>
              </MDBox>
            </Grid>
          )}

          {extraction.extractedData && (
            <Grid item xs={12}>
              <MDBox mb={2}>
                <MDTypography variant="subtitle2" mb={1}>
                  <T context="section heading for the raw extracted data JSON output from AI extraction">
                    Extracted Data
                  </T>
                </MDTypography>
                <pre
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {JSON.stringify(extraction.extractedData, null, 2)}
                </pre>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  return (
    <MDBox p={2}>
      <Tabs
        value={String(selectedExtraction)}
        onValueChange={(value) => setSelectedExtraction(Number(value))}
        className="mb-4"
      >
        <TabsList className="flex-wrap">
          {extractionData.extractions.map((extraction, index) => (
            <TabsTrigger key={extraction.id} value={String(index)}>
              {gt('Extraction {number}', {
                number: index + 1,
                context:
                  'tab label for each extraction result, numbered sequentially',
              })}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {renderExtractionDetails(extractionData.extractions[selectedExtraction])}
    </MDBox>
  );
};

export default ExtractedDataTab;
